import { useState } from "react";
import { getListCustomersQueryKey, getGetCustomerQueryKey, customFetch } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { ModalShell } from "@/components/ui/modal-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { anoDeAniversarioGenerico, aniversarioParaMostrar } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import { titleCasePtBR } from "@/lib/textNormalize";

// Edição do cadastro: nome (diferenciar homônimos), WhatsApp (revalidado no
// provedor) e nascimento. O cadastro é global — vale em todas as lojas.
// Usado em Clientes e no PDV («Informar WhatsApp», por cima da venda); onSaved recebe o cliente como o servidor gravou.
export function EditCustomerModal({ customer, onClose, onSaved }: { customer: any; onClose: () => void; onSaved?: (customer: any) => void }) {
  // Cliente do PDV salvo sem WhatsApp carrega um marcador interno no campo de
  // número — o form começa vazio e o lojista pode cadastrar o número real.
  const hasRealNumber = !String(customer.whatsappNumber || "").startsWith("__avulso__");
  const raw = hasRealNumber ? String(customer.whatsappNumber || "").replace(/\D/g, "") : "";
  const isBr = raw.startsWith("55") && raw.length >= 12;
  const [firstName, setFirstName] = useState(customer.firstName || "");
  const [whatsapp, setWhatsapp] = useState(isBr ? raw.slice(2) : raw);
  const [ddi, setDdi] = useState("55");
  const [birthDate, setBirthDate] = useState(
    customer.birthDate ? String(customer.birthDate).slice(0, 10) : "",
  );
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSave = async () => {
    const digits = whatsapp.replace(/\D/g, "");
    if (!firstName.trim()) {
      toast({ title: "Informe o nome", variant: "destructive" });
      return;
    }
    // Cliente sem número pode continuar sem número (campo vazio = não mexe);
    // mas se digitou algo, tem que ser um número completo.
    if (digits.length > 0 && digits.length < 10) {
      toast({ title: "WhatsApp inválido", description: "Informe um número válido com DDD, ou deixe em branco.", variant: "destructive" });
      return;
    }
    if (hasRealNumber && digits.length === 0) {
      toast({ title: "WhatsApp obrigatório", description: "Este cliente tem número cadastrado — não é possível removê-lo, só corrigir.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const salvo = await customFetch<any>(`/api/customers/${customer.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          ...(digits.length >= 10 ? { whatsappNumber: digits, ddi } : {}),
          birthDate: birthDate || null,
        }),
      });
      queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetCustomerQueryKey(customer.id) });
      toast({ title: "Cliente atualizado!" });
      onSaved?.(salvo);
      onClose();
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: extractApiErrorMessage(err, "Tente de novo em instantes."), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      open={true}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Editar cliente"
      subtitle="Se o número mudar, ele será verificado no WhatsApp."
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="customer-edit-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando…</> : "Salvar"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Nome</Label>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={(e) => setFirstName(titleCasePtBR(e.target.value))}
            autoCapitalize="words"
            placeholder="Nome do cliente"
            data-testid="customer-edit-name"
          />
          <p className="text-xs text-muted-foreground">Dica: use um sobrenome ou apelido pra diferenciar clientes com o mesmo nome.</p>
        </div>
        <div className="space-y-1.5">
          <Label>WhatsApp</Label>
          <WhatsAppInput value={whatsapp} onChange={setWhatsapp} ddi={ddi} onDdiChange={setDdi} />
        </div>
        <div className="space-y-1.5">
          <Label>Data de nascimento <span className="font-normal text-muted-foreground">(opcional)</span></Label>
          <div className="flex gap-2">
            {/* Ano genérico (importado só com dia e mês): mostra DD/MM e salva o valor como está. */}
            <DataBr
              value={birthDate}
              onChange={setBirthDate}
              escrever={(v) => (anoDeAniversarioGenerico(v) ? aniversarioParaMostrar(v) : null)}
              classeDoBloco="flex-1"
              data-testid="customer-edit-birthdate"
            />
            {birthDate && (
              <Button type="button" variant="ghost" size="icon" onClick={() => setBirthDate("")} title="Limpar data">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
